import { PageResult } from "../../../../common/types/api/page-result";
import { CallApi, handleJSONResponse } from "./common";
import { ArtistIndexSchema } from "../../../../services/search-admin/types/index-schema/artist-index-schema";
import { ReleaseIndexSchema } from "../../../../services/search-admin/types/index-schema/release-index-schema";

const callAPI = CallApi("search/admin");

export const SEARCH_PAGE_SIZE = 10;
export const DEFAULT_QUERY = { match_all: {} };

export const searchArtists = async (
  page: number = 0,
  query?: string
): Promise<PageResult<ArtistIndexSchema>> => {
  const response = await callAPI(`/admin-artists`, "POST", {
    from: SEARCH_PAGE_SIZE * page,
    size: SEARCH_PAGE_SIZE,
    query: buildArtistQuery(query),
  });

  return handleSearchResults(response);
};

export const searchReleases = async (
  page: number = 0,
  sortModel: any,
  searchText: string,
  filters: any = {},
  size: number = SEARCH_PAGE_SIZE
): Promise<PageResult<ReleaseIndexSchema>> => {
  const response = await callAPI(`/admin-releases`, "POST", {
    from: size * page,
    size,
    sort: buildSort(sortModel),
    query: buildReleaseQuery(searchText, filters),
  });

  return handleSearchResults(response);
};
const buildSort = (sortModel: any) => {
  if (sortModel.length !== 0) {
    let sortItem = sortModel[0].field;
    let unmapped = "string";

    let keyword = `${sortItem}.keyword`;
    if (sortItem === "artist") {
      keyword = `${sortItem}.name.keyword`;
    }
    if (
      sortItem === "inWatchlist" ||
      sortItem === "backofficeSubmit" ||
      sortItem === "requestCounter" ||
      sortItem === "releaseDate" ||
      sortItem === "ACRScore"
    ) {
      keyword = `${sortItem}`;
      unmapped = "long";
    }
    if (sortItem === "revenue") {
      keyword = `title.keyword`;
      unmapped = "long";
    }
    if (sortItem === "artistPlan") {
      keyword = "artist.subscription.plan.keyword";
    }
    if (sortItem === "country") {
      keyword = "artist.country.name.keyword";
    }

    return [
      {
        [keyword]: {
          order: sortModel[0].sort,
          unmapped_type: unmapped,
        },
      },
    ];
  } else {
    return [];
  }
};
const buildReleaseQuery = (searchText?: string, filters: any = {}) => {
  searchText = searchText?.trim();
  return {
    bool: {
      should: searchText
        ? buildRelaseSearchItems(searchText)
        : filters.releaseIds
        ? buildReleaseIds(filters.releaseIds)
        : DEFAULT_QUERY,
      filter: filters.releaseIds ? undefined: buildFilters(filters),
      must_not: buildMustNot(filters),
      // must: buildMust(filters),
      minimum_should_match: 1,
      boost: 1.0,
    },
  };
};
const buildMustNot = (filters: Record<string, string>): any[] => {
  const result: any[] = [];
  if (filters.backOfficeStatus === "unreviewed") {
    result.push({ exists: { field: "backofficeStatus" } });
  }
  return result;
};
const buildFilters = (filters: Record<string, string>): any[] => {
  const result: any[] = [];
  if (filters.status) {
    result.push({ term: { status: filters.status } });
  }
  if (filters.backOfficeStatus && filters.backOfficeStatus !== "unreviewed") {
    result.push({ term: { backofficeStatus: filters.backOfficeStatus } });
  }
  if (filters.releaseDay && filters.releaseDay !== "ALL") {
    result.push({ term: { releaseDay: filters.releaseDay } });
  }

  if (filters.releaseMonth && filters.releaseMonth !== "ALL") {
    result.push({ term: { releaseMonth: filters.releaseMonth } });
  }

  if (filters.releaseYear && filters.releaseYear !== "ALL") {
    result.push({ term: { releaseYear: filters.releaseYear } });
  }

  if (filters.startDate && filters.endDate) {
    result.push({
      range: {
        releaseDate: {
          gte: new Date(filters.startDate),
          lte: new Date(filters.endDate),
        },
      },
    });
  }

  if (filters.paidItems) {
    result.push({ exists: { field: "paidItems" } });
  }

  if (filters.doneIds?.length) {
    result.push({ bool: { must_not: { terms: { id: filters.doneIds } } } });
  }

  if (filters.artistPlan && filters.artistPlan !== "ALL") {
    result.push({
      nested: {
        path: "artist",
        query: {
          nested: {
            path: "artist.subscription",
            query: {
              [filters.artistPlan === "Pro" ? "terms" : "term"]: {
                "artist.subscription.plan":
                  filters.artistPlan === "Pro"
                    ? ["Pro", "Distribution Pro"]
                    : filters.artistPlan,
              },
            },
          },
        },
      },
    });
  }

  if (filters.genre !== 'ALL') {
    result.push({
      term: {
        "genres.value.keyword": filters.genre,
      },
    });
    result.push({
      term: {
        "genres.fuga.keyword": filters.genre,
      },
    });
  }


  if (filters.popularity) {
    result.push({
        nested: {
          path: "tracks",
          query: {
            range: {
              "tracks.mlClassifierResult.popularity": {
                gte: Number(filters.popularity) / 100,
              }
            }
          }
        }
    });
  }

  if (filters.type !== 'ALL') {
    result.push({
      nested: {
        path: "tracks",
        query: {
          term: {
            "tracks.type": filters.type
          }
        }
      }
    });
  }

  if (filters.isExplicit !== 'ALL') {
    result.push({
      nested: {
        path: "tracks",
        query: {
          term: {
            "tracks.isExplicit": filters.isExplicit === 'EXPLICIT' ? true : false
          }
        }
      }
    });
  }

  if (filters.trackVersion !== 'ALL') {
    result.push({
      nested: {
        path: "tracks",
        query: {
          term: {
            "tracks.trackVersion.keyword": filters.trackVersion
          }
        }
      }
    });
  }

  if (filters.language !== 'ALL') {
    result.push({
      nested: {
        path: "tracks",
        query: {
          term: {
            "tracks.language.keyword": filters.language
          }
        }
      }
    });
  }


  return result;
};

const buildRelaseSearchItems = (searchText: string) => {
  return [
    {
      match_phrase: {
        title: searchText,
      },
    },
    {
      match: {
        "artists.name": searchText,
      },
    },
    {
      match: {
        "artists.email": searchText,
      },
    },
    {
      match_phrase: {
        UPCEAN: searchText,
      },
    },
  ];
};
// const buildRelaseQueryItems = (query: string) => {
//   return [
//     { match: { title: query } },
//     { term: { UPCEAN: query } },
//     { term: { id: query } },
//     {
//       nested: {
//         path: "artist",
//         query: {
//           bool: {
//             should: [
//               { match: { "artist.name": query } },
//               { match: { "artist.email": query } },
//             ],
//           },
//         },
//         score_mode: "avg",
//       },
//     },
//     {
//       nested: {
//         path: "tracks",
//         query: {
//           bool: {
//             should: [
//               { match: { "tracks.title": query } },
//               { term: { "tracks.isrc": query } },
//             ],
//           },
//         },
//         score_mode: "avg",
//       },
//     },
//   ];
// };

const buildArtistQuery = (query?: string) => {
  if (!query?.trim()) {
    return DEFAULT_QUERY;
  }
  return {
    bool: {
      should: [
        { match: { name: query } },
        { match: { email: query } },
        { match: { "releases.UPCEAN": query } },
        { match: { "releases.title": query } },
      ],
    },
  };
};

const handleSearchResults = async <T>(
  response: Response
): Promise<PageResult<T>> => {
  const data = await handleJSONResponse(response);

  return {
    results: data.hits.hits.map((i: any) => i._source),
    total: data.hits.total.value,
    next: "",
  };
};

// const buildMust = (filters: Record<string, string | string[]>): any[] => {
//   const query = [];
//   if (filters.artistPlan && filters.artistPlan !== "ALL") {
//     query.push({
//       match: {
//         "artist.subscription.plan": filters.artistPlan,
//       },
//     });
//   }
//   return query;
// };

const buildReleaseIds = (releaseIds: string[]): any[] => {
  return releaseIds.map((id) => ({
    match: { id },
  }));
};
